import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TrackPlateService } from 'src/app/services/track-plate.service';
import { SavedPlatesService } from 'src/app/services/saved-plates.service';

// One call-to-action in the registry panel. `action` maps to an EXISTING
// capability (favourite / watchlist) or a deferred placeholder — we never wire
// new backend infrastructure here.
interface RegistryCta {
  label: string;
  type: 'primary' | 'ghost';
  action: 'save' | 'track' | 'alert' | 'collection' | 'how';
}

// A "what you can do" feature row (used by the watchlist / registry variants).
interface RegistryFeature {
  icon: string; // a Material icon name (rendered via <mat-icon>)
  title: string;
  desc: string;
}

interface RegistryConfig {
  eyebrow: string;
  heading: string;
  body: string;
  ctas: RegistryCta[];
  // Platefolio "Own this registration?" variant uses an ownership tick list...
  ownerIntro?: string | null;
  ownerList?: string[] | null;
  // ...while the watchlist / registry variants use a feature list instead.
  features?: RegistryFeature[] | null;
}

// "Platefolio / Own this registration?" — the registry panel from the
// "PlateX 33.3" prototype, ported faithfully and made conditional on the plate
// state the rest of the page already knows. Save and Track go through the
// shared SavedPlatesService and TrackPlateService; the remaining
// CTAs are deferred visual slots, matching the prototype's intent without
// inventing valuation/alert backends.
//
// Variant selection (mirrors the prototype's ownershipModel):
//   * Buyable now (priced or dealer-listed) -> Platefolio "Own this
//     registration?" (claim/build Platefolio AFTER purchase).
//   * New Issue, no price                    -> "Keep track of this plate".
//   * On Vehicle / On Retention (assigned)   -> "Track this registration".
//   * Unknown                                -> Platefolio (default).
@Component({
  selector: 'pla-pdv2-platefolio',
  templateUrl: './platefolio.component.html',
  styleUrls: ['./platefolio.component.scss'],
})
export class PlatefolioComponent {
  @Input() public registration: string | null = null;
  @Input() public formattedRegistration: string | null = null;
  // The page's 4-status taxonomy value: 'New Issue' | 'On Vehicle' |
  // 'On Retention' | 'Unknown'.
  @Input() public status: string | null = null;
  // True when the plate carries a PlateX stock/asking price.
  @Input() public hasPrice: boolean = false;
  // True when the dealer marketplace surfaced at least one live listing.
  @Input() public hasListings: boolean = false;


  constructor(
    private router: Router,
    private trackPlate: TrackPlateService,
    private savedPlates: SavedPlatesService
  ) {}

  // Already saved? Shared shortlist (SavedPlatesService), same as the hero heart.
  public get isSaved(): boolean {
    return this.savedPlates.isSaved(this.registration);
  }


  // Pick the variant for the current plate state.
  public get config(): RegistryConfig {
    // Buyable right now (priced stock or live dealer listings): the user does
    // not own it yet, so we frame the Platefolio as the post-purchase benefit.
    if (this.hasPrice || this.hasListings) return this.platefolioConfig();
    switch (this.status) {
      case 'New Issue':
        return this.watchlistConfig();
      case 'On Vehicle':
      case 'On Retention':
        return this.registryConfig();
      default:
        return this.platefolioConfig();
    }
  }

  private get trackLabel(): string {
    return this.trackPlate.isTracked(this.registration) ? 'Tracking' : 'Track plate';
  }

  private get plateLabel(): string {
    return this.formattedRegistration || this.registration || 'this plate';
  }

  // --- Variants (copy ported verbatim from the prototype, with the plate mark
  // substituted for the prototype's hardcoded example) ---

  private platefolioConfig(): RegistryConfig {
    return {
      eyebrow: 'Platefolio',
      heading: 'Own this registration?',
      body:
        `After you buy ${this.plateLabel}, claim it on PlateX to build your ` +
        'Platefolio. Keep a record of what you own, add photographs and ' +
        'track its market value over time.',
      ctas: [
        { type: 'primary', label: 'Save to your shortlist', action: 'save' },
        { type: 'ghost', label: 'How Platefolio works', action: 'how' },
      ],
      ownerIntro: 'After purchase you can',
      ownerList: [
        'Claim ownership on PlateX',
        'Upload photographs',
        'Build your Platefolio',
        'Track market values',
        'Follow similar registrations',
        'Receive market updates',
      ],
    };
  }

  private watchlistConfig(): RegistryConfig {
    return {
      eyebrow: 'PlateX watchlist',
      heading: 'Keep track of this plate',
      body:
        "This registration isn't owned yet. Save it, track availability, and " +
        'get alerted the moment the price moves or a dealer lists it.',
      ctas: [
        { type: 'primary', label: 'Save this plate', action: 'save' },
        { type: 'ghost', label: this.trackLabel, action: 'track' },
      ],
      features: [
        {
          icon: 'bookmark_border',
          title: 'Save to your list',
          desc: 'Build a shortlist to compare.',
        },
        {
          icon: 'visibility',
          title: 'Track availability',
          desc: 'Know when it can be bought.',
        },
        {
          icon: 'notifications_active',
          title: 'Price & status alerts',
          desc: 'Notified on any change.',
        },
        {
          icon: 'show_chart',
          title: 'Index movement',
          desc: 'Segment trend updates.',
        },
      ],
    };
  }

  private registryConfig(): RegistryConfig {
    return {
      eyebrow: 'PlateX registry',
      heading: 'Track this registration',
      body:
        'This registration is already assigned. Track its market value over ' +
        'time and follow movement in its segment within the PlateX index.',
      ctas: [
        { type: 'primary', label: this.trackLabel, action: 'track' },
        { type: 'ghost', label: 'Add to collection', action: 'collection' },
      ],
      features: [
        {
          icon: 'show_chart',
          title: 'Track market value',
          desc: 'Indexed against PlateX data.',
        },
        {
          icon: 'bookmark_border',
          title: 'Save & shortlist',
          desc: 'Keep it on your list.',
        },
        {
          icon: 'collections_bookmark',
          title: 'Manage a collection',
          desc: 'Keep your plates together.',
        },
        {
          icon: 'notifications_active',
          title: 'Index alerts',
          desc: 'Segment movement updates.',
        },
      ],
    };
  }

  // --- CTA wiring ---

  public runCta(cta: RegistryCta): void {
    switch (cta.action) {
      case 'save':
        this.save();
        return;
      case 'track':
        this.track();
        return;
      case 'how':
      case 'alert':
      case 'collection':
        // Deferred visual slots — same posture as the hero "Track" placeholder.
        // No alert/collection backend is wired here yet.
        return;
    }
  }

  // Save to the shortlist (no sign-in needed; never creates alerts). Adds
  // only: a second press here never un-saves (the hero heart toggles).
  private save(): void {
    if (this.isSaved) return;
    this.savedPlates.save(this.registration).subscribe();
  }

  // Track plate: the shared tracking system (same as the hero row).
  private track(): void {
    if (this.trackPlate.isTracked(this.registration)) return;
    this.trackPlate.track(this.registration, this.formattedRegistration || this.registration);
  }
}
